import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import MasterSelect from '../components/MasterSelect';
import PriceModeFields from '../components/PriceModeFields';
import ReceiptScanner from '../components/ReceiptScanner';
import SeasonSelector from '../components/SeasonSelector';
import { PaymentModal } from './PaymentsPage';

const CATEGORIES = [
  { value: '',            label: 'Tümü' },
  { value: 'fuel',        label: '⛽ Yakıt' },
  { value: 'seed',        label: '🌱 Tohum' },
  { value: 'fertilizer',  label: '🧪 Gübre' },
  { value: 'pesticide',   label: '💧 İlaç' },
  { value: 'material',    label: '🔧 Malzeme' },
  { value: 'other',       label: '📦 Diğer' },
];

const UNITS = ['kg','ton','litre','adet','çuval','dekar'];

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('tr-TR') : '';
}

// Keep quantity / unit_price / total_amount consistent regardless of which the
// farmer typed. priceMode 'unit' → total = qty × unit; 'total' → unit = total / qty.
function recalcPricing(f) {
  const q = parseFloat(f.quantity);
  if (f.priceMode === 'total') {
    const total = parseFloat(f.totalAmount);
    const up = (q > 0 && !isNaN(total)) ? total / q : '';
    return { ...f, unitPrice: up === '' ? '' : String(up) };
  }
  const up = parseFloat(f.unitPrice);
  const total = (q > 0 && !isNaN(up)) ? q * up : '';
  return { ...f, totalAmount: total === '' ? '' : String(total) };
}

const EMPTY_FORM = {
  itemName: '', category: 'material', quantity: '', unit: 'kg',
  unitPrice: '', totalAmount: '', priceMode: 'unit',
  paymentStatus: 'paid', contactName: '', notes: '',
  transactionDate: new Date().toISOString().split('T')[0],
  masterProductId: '', seasonId: '',
};

export default function PurchasesPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [history, setHistory] = useState(null); // null = closed, [] = open

  const openHistory = async () => {
    setHistory([]);
    try {
      const r = await api.get('/receipts');
      setHistory(r.data.data || []);
    } catch { setHistory([]); }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = catFilter ? `?category=${catFilter}` : '';
    api.get(`/purchases${params}`)
      .then(r => setItems(r.data.data))
      .catch(() => toast.error('Alımlar yüklenemedi'))
      .finally(() => setLoading(false));
  }, [catFilter]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    return ['quantity', 'unitPrice', 'totalAmount', 'priceMode'].includes(k) ? recalcPricing(next) : next;
  });

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    try {
      const r = await api.get(`/payments/purchase/${id}`);
      setPayments(r.data.data);
    } catch { setPayments([]); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.itemName || !form.quantity || !form.unitPrice) {
      return toast.error('Zorunlu alanları doldurun');
    }
    setSaving(true);
    try {
      await api.post('/purchases', form);
      toast.success('Alım kaydedildi ✓');
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;
    try {
      await api.delete(`/purchases/${id}`);
      toast.success('Silindi');
      load();
    } catch { toast.error('Silinemedi'); }
  };

  const total = items.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);

  return (
    <div>
      <div className="card-row" style={{ marginBottom: 12 }}>
        <h2 className="section-title" style={{ margin: 0 }}>{t('purchases.title')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={openHistory}
            style={{ background: 'none', border: 'none', color: 'var(--green-mid)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
            🧾 Geçmiş
          </button>
          <span style={{ fontWeight: 800, color: '#c0392b', fontSize: '1.05rem' }}>{fmt(total)}</span>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="chip-group">
        {CATEGORIES.map(c => (
          <button
            key={c.value}
            className={`chip${catFilter === c.value ? ' active' : ''}`}
            onClick={() => setCatFilter(c.value)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="card">
        {loading ? (
          <div className="empty-state"><p>Yükleniyor...</p></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🛒</div>
            <p>Henüz alım kaydı yok</p>
          </div>
        ) : (
          items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main" onClick={() => toggleExpand(item.id)} style={{ cursor: 'pointer' }}>
                <div className="txn-name">{item.item_name}</div>
                <div className="txn-meta">
                  {fmtDate(item.transaction_date)} · {item.quantity} {item.unit}
                  {item.contact_name && ` · ${item.contact_name}`}
                </div>
                <div className="txn-meta" style={{ marginTop: 2 }}>
                  {item.quantity} {item.unit} × {fmt(item.unit_price)} = <b>{fmt(item.total_amount)}</b>
                </div>
                <div style={{ marginTop: 4 }}>
                  <span className={`badge badge-${item.payment_status}`}>
                    {item.payment_status === 'paid' ? 'Ödendi' : item.payment_status === 'pending' ? 'Bekliyor' : 'Kısmi'}
                  </span>
                  {item.payment_count > 1 && (
                    <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: 4 }}>
                      {item.payment_count} taksit
                    </span>
                  )}
                </div>
                {expandedId === item.id && (
                  <div style={{ marginTop: 8, padding: 8, background: '#f9f9f9', borderRadius: 8, fontSize: '0.8rem' }}>
                    {item.payment_count > 0 && (
                      <div style={{ marginBottom: 6, color: '#666' }}>
                        {payments.map(p => (
                          <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span>{fmtDate(p.payment_date)} · {p.payment_method}</span>
                            <span style={{ fontWeight: 700 }}>{fmt(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {item.payment_status !== 'paid' && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#e74c3c', fontWeight: 700 }}>
                          Kalan: {fmt(parseFloat(item.total_amount) - parseFloat(item.amount_paid || 0))}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); setPayTarget(item); }}
                          style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8,
                            padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
                          ➕ Ödeme Ekle
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <span className="txn-amount expense">{fmt(item.total_amount)}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {item.payment_status !== 'paid' && (
                    <button onClick={() => setPayTarget(item)}
                      style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '3px 8px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                      💳 Öde
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(item.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                  >🗑️</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Receipt scanner button — left of the FAB */}
      <button className="btn-fab" onClick={() => setShowScanner(true)}
        style={{ right: 84, background: '#fff', color: 'var(--green-mid)', border: '2px solid var(--green-mid)' }}
        title="Fiş tara">📷</button>
      {/* FAB */}
      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>

      {showScanner && (
        <ReceiptScanner
          onClose={() => setShowScanner(false)}
          onSuccess={() => { setShowScanner(false); load(); toast.success('✅ Fişten alım(lar) eklendi'); }}
          onManualEntry={() => { setShowScanner(false); setShowModal(true); }}
        />
      )}

      {history !== null && (
        <Modal title="🧾 Fiş Geçmişi" onClose={() => setHistory(null)}>
          {history.length === 0 ? (
            <div className="empty-state"><p>Henüz taranmış fiş yok</p></div>
          ) : history.map(h => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f2f2f2', fontSize: '0.84rem' }}>
              <span>{h.status === 'confirmed' ? '✅' : h.status === 'rejected' ? '❌' : h.status === 'partial' ? '⚠️' : '🕒'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{h.extracted_vendor || 'Bilinmeyen satıcı'}</div>
                <div style={{ fontSize: '0.72rem', color: '#888' }}>
                  {fmtDate(h.created_at)} · {h.item_count} kalem
                  {h.status === 'confirmed' ? ' · kaydedildi' : h.status === 'pending' ? ' · bekliyor' : ''}
                </div>
              </div>
              {h.extracted_total != null && <span style={{ fontWeight: 700, color: '#c0392b' }}>{fmt(h.extracted_total)}</span>}
            </div>
          ))}
        </Modal>
      )}

      {/* Add modal */}
      {payTarget && (
        <PaymentModal
          entityType="purchase" entityId={payTarget.id}
          entityName={payTarget.item_name} totalAmount={payTarget.total_amount}
          direction="out" onClose={() => setPayTarget(null)} onSaved={load}
        />
      )}

      {showModal && (
        <Modal title="Yeni Alım Ekle" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <MasterSelect
              type="product"
              label="Ürün / Malzeme Adı"
              value={form.itemName}
              onChange={v => set('itemName', v)}
              onSelect={p => { set('category', p.category); set('unit', p.unit || 'kg'); set('masterProductId', p.id); }}
              placeholder="Örn: Mazot, Tohum, Gübre..."
              required
              priorityCategory="material"
              usageType="buy"
            />
            <MasterSelect
              type="contact"
              filter={{ type: 'seller' }}
              label="Satıcı"
              value={form.contactName}
              onChange={v => set('contactName', v)}
              placeholder="Satıcı seçin veya yazın..."
            />
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 2 }}>🌾 Sezon</label>
            <SeasonSelector value={form.seasonId} onChange={v => set('seasonId', v)} />
            <div className="form-row">
              <div className="form-group">
                <label>Kategori</label>
                <select value={form.category} onChange={e => set('category', e.target.value)} disabled={!!form.itemName}>
                  {CATEGORIES.slice(1).map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Tarih</label>
                <input
                  type="date"
                  value={form.transactionDate}
                  onChange={e => set('transactionDate', e.target.value)}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Miktar *</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.quantity}
                  onChange={e => set('quantity', e.target.value)}
                  placeholder="0"
                  required
                />
              </div>
              <div className="form-group">
                <label>Birim</label>
                <select value={form.unit} onChange={e => set('unit', e.target.value)}>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <PriceModeFields
              mode={form.priceMode} unit={form.unit}
              quantity={form.quantity} unitPrice={form.unitPrice} totalAmount={form.totalAmount}
              onMode={m => set('priceMode', m)}
              onUnitPrice={v => set('unitPrice', v)}
              onTotal={v => set('totalAmount', v)}
            />
            <div className="form-group">
              <label>Ödeme Durumu</label>
              <select value={form.paymentStatus} onChange={e => set('paymentStatus', e.target.value)}>
                <option value="paid">✅ Ödendi</option>
                <option value="pending">⏳ Bekliyor</option>
                <option value="partial">🔶 Kısmi</option>
              </select>
            </div>
            <div className="form-group">
              <label>Notlar</label>
              <textarea
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                placeholder="Ek bilgi..."
                rows={2}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setShowModal(false)}>
                İptal
              </button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
                {saving ? 'Kaydediliyor...' : '💾 Kaydet'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
